import { useState } from "react";
import { Mail, Phone, MapPin, Send, Clock, Facebook, Instagram } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

// TikTok icon
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className || "w-6 h-6"}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const SOCIAL_LINKS = {
  facebook: "https://www.facebook.com/profile.php?id=61579538215508",
  instagram: "https://www.instagram.com/repticraftstore",
  tiktok: "https://www.tiktok.com/@repticraftstore",
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSending(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success("Message sent! We'll get back to you within 24-48 hours.");
    setFormData({ name: "", email: "", subject: "", message: "" });
    setSending(false);
  };

  return (
    <div className="min-h-screen pt-24 px-4 bg-[#0A0A0A]">
      <div className="max-w-5xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-6">
            Get in Touch
          </h1>
          <p className="text-xl text-[#F4F1DE]/70 max-w-2xl mx-auto">
            Have a question about your order, need a custom quote, or just want to chat about reptiles? 
            We'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            {/* Email */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E07A5F]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#E07A5F]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#F4F1DE] mb-1">Email Us</h3>
                  <a 
                    href="mailto:repticraftstore@gmail.com" 
                    className="text-[#E07A5F] hover:underline break-all"
                  >
                    repticraftstore@gmail.com
                  </a>
                  <p className="text-[#F4F1DE]/50 text-sm mt-2">
                    Best for quotes and detailed enquiries
                  </p>
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E07A5F]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#E07A5F]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#F4F1DE] mb-1">Phone</h3>
                  <a
                    href="tel:+447426848476"
                    className="text-[#E07A5F] hover:underline"
                    data-testid="contact-phone-link"
                  >
                    07426 848476
                  </a>
                  <p className="text-[#F4F1DE]/50 text-xs mt-1">
                    +44 7426 848476 (international)
                  </p>
                  <p className="text-[#F4F1DE]/50 text-sm mt-2">
                    Mon-Fri, 9am-5pm GMT
                  </p>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E07A5F]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#E07A5F]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#F4F1DE] mb-1">Workshop</h3>
                  <p className="text-[#F4F1DE]/70">
                    [Address TBC]<br />
                    United Kingdom
                  </p>
                  <p className="text-[#F4F1DE]/50 text-sm mt-2">
                    Collection by appointment only
                  </p>
                </div>
              </div>
            </div>

            {/* Response Time */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E07A5F]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[#E07A5F]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#F4F1DE] mb-1">Response Time</h3>
                  <p className="text-[#F4F1DE]/70">
                    We typically respond within 24-48 hours during business days.
                  </p>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="font-bold text-[#F4F1DE] mb-4">Follow Us</h3>
              <div className="flex gap-4">
                <a
                  href={SOCIAL_LINKS.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-[#1877F2]/20 rounded-lg flex items-center justify-center text-[#1877F2] hover:bg-[#1877F2]/30 transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-6 h-6" />
                </a>
                <a
                  href={SOCIAL_LINKS.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-[#E4405F]/20 rounded-lg flex items-center justify-center text-[#E4405F] hover:bg-[#E4405F]/30 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-6 h-6" />
                </a>
                <a
                  href={SOCIAL_LINKS.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-[#00f2ea]/20 rounded-lg flex items-center justify-center text-[#00f2ea] hover:bg-[#00f2ea]/30 transition-colors"
                  aria-label="TikTok"
                >
                  <TikTokIcon className="w-6 h-6" />
                </a>
              </div>
              <p className="text-[#F4F1DE]/50 text-sm mt-3">
                Follow us for build updates, care tips & more!
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form 
              onSubmit={handleSubmit} 
              className="bg-[#121212] border border-white/10 p-8"
              data-testid="contact-form"
            >
              <h2 className="text-2xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-6">
                Send us a Message
              </h2>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label htmlFor="name" className="text-[#F4F1DE]/80">Your Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-2 bg-[#0A0A0A] border-white/10 text-[#F4F1DE] h-12"
                    placeholder="John Smith"
                    required
                    data-testid="contact-name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#F4F1DE]/80">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="mt-2 bg-[#0A0A0A] border-white/10 text-[#F4F1DE] h-12"
                    placeholder="john@example.com"
                    required
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div className="mb-6">
                <Label htmlFor="subject" className="text-[#F4F1DE]/80">Subject</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  className="mt-2 bg-[#0A0A0A] border-white/10 text-[#F4F1DE] h-12"
                  placeholder="Custom enclosure enquiry"
                  data-testid="contact-subject"
                />
              </div>

              <div className="mb-6">
                <Label htmlFor="message" className="text-[#F4F1DE]/80">Message *</Label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  className="mt-2 w-full h-40 bg-[#0A0A0A] border border-white/10 text-[#F4F1DE] p-4 resize-none focus:outline-none focus:ring-2 focus:ring-[#E07A5F]/50 rounded-md"
                  placeholder="Tell us about your project or question..."
                  required
                  data-testid="contact-message"
                />
              </div>

              <Button
                type="submit"
                disabled={sending}
                className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white py-6 font-bold uppercase tracking-widest"
                data-testid="contact-submit"
              >
                {sending ? (
                  <span className="spinner" />
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>

            {/* FAQ Teaser */}
            <div className="mt-8 bg-[#121212] border border-white/10 p-6">
              <h3 className="font-bold text-[#F4F1DE] mb-3">Common Questions</h3>
              <div className="space-y-3 text-[#F4F1DE]/70 text-sm">
                <p>
                  <span className="font-medium text-[#F4F1DE]">How long does delivery take?</span><br />
                  UK orders typically arrive within 5-10 working days. EU delivery takes 7-18 days depending on location.
                </p>
                <p>
                  <span className="font-medium text-[#F4F1DE]">Can you make custom sizes?</span><br />
                  Absolutely! Use our configurator to specify your exact dimensions, or contact us for special requirements.
                </p>
                <p>
                  <span className="font-medium text-[#F4F1DE]">Do you ship internationally?</span><br />
                  We ship to the UK and all EU countries. Contact us for other destinations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
